import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig, createGridDropDownCommitAfter } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, getRowStyleFlags, IndexParameterCell, onGridReady } from "src/utils/grid/gridStyleUtils";
import { usePUR2010_R01Store } from "./Store_PUR2010_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultPUR2010_R01DetailDto, PUR2010_R01DetailDto } from "./Dto_PUR2010_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { getItemPopupList } from "src/apis/itemApi";
import { ItemDto } from "src/dtos/item/itemDto";
import { showWarning } from "src/utils/toast/toastUtil";

import { useDetailTableWithItemSearch } from "src/hooks/detail-table/useDetailTableWithItemSearch";

import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { validateString } from "src/utils/string/stringUtil";
import { useCodeSearch } from "src/hooks/useCodeSearch";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";
import { usePUR2010_R01Modal } from "./hooks/usePUR2010_R01Modal";
import { usePUR2010_R01CellChange } from "./hooks/PUR2010_R01CellChange";

const COLUMN_STATE_KEY = 'PUR2010_R01-detail-grid-column-state';


interface Props {
	gridRef: React.RefObject<AgGridReact<PUR2010_R01DetailDto> | null>;
	defaultPUR2010_R01DetailRow: PUR2010_R01DetailDto;
}

export const DetailTable_PUR2010_R01 = (props: Props) => {

	const { gridRef, defaultPUR2010_R01DetailRow } = props;

	const { 
			PUR2010_R01DetailList, setPUR2010_R01DetailList, 
			selectedPUR2010_R01DetailList, setSelectedPUR2010_R01DetailList, removeSelectedPUR2010_R01DetailList, addSelectedPUR2010_R01DetailList, initSelectedPUR2010_R01DetailList,
			deletedPUR2010_R01DetailList, setDeletedPUR2010_R01DetailList, addDeletedPUR2010_R01DetailList, removeDeletedPUR2010_R01DetailList,
			insertPUR2010_R01DetailList, updatePUR2010_R01DetailList, removePUR2010_R01DetailList,
			detailColumnState, setDetailColumnState,
			originalPUR2010_R01DetailList, setOriginalPUR2010_R01DetailList,
			detailScrollTop, setDetailScrollTop,
		} = usePUR2010_R01Store();
	const [clickable, setClickable] = useState(true);

	const { unitList } = useCommonCodeStore();

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: PUR2010_R01DetailList,
		defaultGridData: defaultPUR2010_R01DetailRow,
		selectedRow: selectedPUR2010_R01DetailList,
		originalGridData: originalPUR2010_R01DetailList,
		updateRow: (index: number, data: any) => updatePUR2010_R01DetailList(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
	disabledCells: ['itemNm', 'itemSpec', 'locationNm', 'modelNo', 'billUnit', 'billAmt'],
	insertedDisabledCells: [''],
	commonDisabledCells: ['itemCd'],
	
	pointerCells: [''],
	insertedPointerCells: ['itemCd', 'billQty', 'billPrice', 'dcAmt', 'billAmt'],
	commonPointerCells: ['billQty', 'billPrice', 'dcAmt', 'billAmt'],
	
	alignLeftCells: ['']
}), []);

	const headerNames: Record<string, string> = {
		rowIndex: "",
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		locationNm: '보관위치',
		modelNo: '품번',
		billQty: '수량',
		billUnit: '단위',
		billPrice: '단가',
		dcAmt: '할인액',
		billAmt: '금액',
	}

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<PUR2010_R01DetailDto>({
			idKey: 'valueIndex',
			selectedList: selectedPUR2010_R01DetailList,
			deletedList: deletedPUR2010_R01DetailList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [
		selectedPUR2010_R01DetailList, deletedPUR2010_R01DetailList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	]);

	const { setCodeSearchModalProps } = usePUR2010_R01Modal();

	// 품목 검색 함수 (useDetailTableWithItemSearch용)
	const { searchExactCode } = useCodeSearch({

	});

	const detailTableHandlers = usePUR2010_R01CellChange({
		PUR2010_R01DetailList,
		originalPUR2010_R01DetailList,
		updatePUR2010_R01DetailList,
		gridRef,
		setCodeSearchModalProps,
	});

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['itemCd', 'itemNm', 'itemSpec', 'modelNo', 'billQty', 'billUnit', 'billPrice', 'dcAmt'];
	const defaultCodeSearchColumns = ['itemCd', 'itemNm', 'itemSpec', 'modelNo'];

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		InsertRowHandler({
			event: e,
			dataList: PUR2010_R01DetailList,
			rowData: PUR2010_R01DetailList[rowIndex],
			insertRow: insertPUR2010_R01DetailList,
			defaultRowData: defaultPUR2010_R01DetailRow,
			setClickable
		});
	}, [PUR2010_R01DetailList, insertPUR2010_R01DetailList, defaultPUR2010_R01DetailRow, setClickable]);
	
	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<PUR2010_R01DetailDto>(defaultEditableColumns),
	[defaultEditableColumns],);

	const baseColumnDefs: ColDef<PUR2010_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: PUR2010_R01DetailDto) => renderIndexCell(
				params, 
				(innerParams: PUR2010_R01DetailDto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedPUR2010_R01DetailList,
						selectedList: selectedPUR2010_R01DetailList,
						dataList: PUR2010_R01DetailList
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
					} else { 
						insertRowHandler(rowIndex, e);
					}
				},
				(rowData) => RemoveRowHandler( 
					gridRef,
					PUR2010_R01DetailList, 
					rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
					setClickable, 
					removePUR2010_R01DetailList, 
					(valueIndex) => addDeletedPUR2010_R01DetailList([valueIndex.toString()])
				),
			)
		),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 77,
			editable: ((params) => params.data?.inserted === true),
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 161,
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 127,
			editable: false,
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			editable: false,
		} as ColDef<PUR2010_R01DetailDto>,
		{
			headerName: '품번', colId: 'modelNo', field: 'modelNo', width: 130,
			editable: false,
		},
		{
			headerName: '수량', colId: 'billQty', field: 'billQty', width: 60,
			editable: (params) => PUR2010_R01DetailList[params.node.rowIndex!] !== undefined,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '단위', colId: 'billUnit', field: 'billUnit', width: 60,
			...CommonCodeColumn.options(unitList, false, CommonCodeColumn.change(updatePUR2010_R01DetailList), handleGridDropDownCommitAfter),
		},
		{
			headerName: '단가', colId: 'billPrice', field: 'billPrice', width: 65,
			editable: (params) => PUR2010_R01DetailList[params.node.rowIndex!] !== undefined,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '할인액', colId: 'dcAmt', field: 'dcAmt', width: 65,
			editable: (params) => PUR2010_R01DetailList[params.node.rowIndex!] !== undefined,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '금액', colId: 'billAmt', field: 'billAmt', width: 96,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		// ...IndexParameterCell(),
	], [
		deletedPUR2010_R01DetailList, PUR2010_R01DetailList,
	]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<PUR2010_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<PUR2010_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState, headerNames]);
	

	const handleRowClick = useRowSelectionHandler({
		dataList: PUR2010_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedPUR2010_R01DetailList,
		deletedList: deletedPUR2010_R01DetailList,
		addSelected: addSelectedPUR2010_R01DetailList,
		removeSelected: removeSelectedPUR2010_R01DetailList,
		clickable, setClickable
	});

	/**
	 * 품목 검색 기능이 포함된 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 품목 코드 더블클릭으로 품목 검색 모달 열기
	 * - 키다운 네비게이션 (엔터 시 수량 필드로 이동, 방향키 네비게이션)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 * @param itemSearchConfig - 품목 검색 설정
	 * @param keyDownConfig - 키다운 네비게이션 설정
	 */
	const cellEventHandlers = useDetailTableWithItemSearch<PUR2010_R01DetailDto>({
		detailList: PUR2010_R01DetailList,
		editableColumns: defaultEditableColumns,
		itemSearchConfig: {
			config: {
				category: '품목', // 검색 카테고리
				responseMapping: {
					// 검색 결과 → 테이블 필드 매핑
					itemCd: 'itemCd',      // 품목코드
					itemNm: 'itemNm',      // 품명
					itemSpec: 'itemSpec',  // 규격
					modelNo: 'modelNo',    // 품번
					itemUnit: 'billUnit',  // 단위
					itemCost: 'billPrice',  // 단가
					locationCd: 'locationCd',
					locationNm: 'locationNm',
				},
				clearFields: {
					// 품목 검색 시 초기화할 필드들
					itemCd: "", itemNm: "", itemSpec: "", modelNo: "", billUnit: "",
					billPrice: 0, dcAmt: 0, billAmt: 0,
					locationCd: "", locationNm: "",
				},
				searchFunction: (code: string, onSuccess?: (item: any) => void) => 
					searchExactCode('item', code, onSuccess) // 품목 검색 함수
			},
			settingCodeSearchModalProps: detailTableHandlers.settingCodeSearchModalProps, // 모달 설정 함수
		},
		constraints: {
			notEdit: (params) => {
				if(params.data?.inserted === true) return true;
				return false;
			}
		},
		keyDownConfig: {
			qtyField: 'billQty',         // 엔터 키 시 수량 필드로 이동
			enableArrowNavigation: true  // 방향키 네비게이션 활성화
		}
	});

	const isRowDraggingRef = useRef(false);

	const handlePostSort = useCallback((params: PostSortRowsParams<PUR2010_R01DetailDto>) => {
		// 드래그 중에는 postSortRows에 의한 강제 정렬을 막아 드래그가 눈에 보이도록 함
		if (isRowDraggingRef.current) return;
		postSortHandler(params, PUR2010_R01DetailList, originalPUR2010_R01DetailList, setPUR2010_R01DetailList);
	}, [PUR2010_R01DetailList, originalPUR2010_R01DetailList, ]);

	const pinnedBottomRowData = useMemo(() => {

		const totalQty : number = PUR2010_R01DetailList
			.reduce((sum, item) => sum + (deletedPUR2010_R01DetailList.includes(item.itemCd) ? 0 : Number(item.billQty) || 0), 0);
		const totalAmt : number = PUR2010_R01DetailList
			.reduce((sum, item) => sum + (deletedPUR2010_R01DetailList.includes(item.itemCd) ? 0 : Number(item.billAmt) || 0), 0);

		return [{
			itemCd: PUR2010_R01DetailList.length.toLocaleString() + "건",
			billQty: totalQty,
			billAmt: totalAmt
		}];	  
	}, [PUR2010_R01DetailList, deletedPUR2010_R01DetailList,]);

	return (
		<GridContextWrapper<PUR2010_R01DetailDto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={PUR2010_R01DetailList}
			setPaddedGridData={setPUR2010_R01DetailList}
			initSelected={initSelectedPUR2010_R01DetailList}
			defaultRowData={defaultPUR2010_R01DetailRow}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}	
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<PUR2010_R01DetailDto>
				ref={gridRef}
				rowData={PUR2010_R01DetailList}	
				getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
				columnDefs={columnDefs}		
				defaultColDef={cellCssDefaultColDef}

				rowClassRules={createRowClassRules<PUR2010_R01DetailDto>({
					idKey: 'valueIndex',
					selectedList: selectedPUR2010_R01DetailList,
					deletedList: deletedPUR2010_R01DetailList
				})}

				onRowClicked={handleRowClick}
				onCellClicked={cellEventHandlers.handleCellClick}
				onCellDoubleClicked={cellEventHandlers.handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={detailTableHandlers.handleCellChange}
				onCellEditingStopped={detailTableHandlers.handleCellEditingStopped}
				pinnedBottomRowData={pinnedBottomRowData}
                rowDragManaged={true}
                rowDragEntireRow={true}
				onRowDragEnter={() => { isRowDraggingRef.current = true; }}
                onRowDragEnd={(e: any) => {
                    const api = e.api;
                    // 정렬 해제하여 사용자가 정한 순서를 유지
                    if (api.applyColumnState) {
                        api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
                    }
                    const reordered: PUR2010_R01DetailDto[] = [];
                    api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
                        if (!node.data) return;
                        reordered.push({
                            ...node.data,
                            orderIndex: idx + 1,
                            orderIndexSeq: 0,
                        });
                    });
                    setPUR2010_R01DetailList(reordered);
					isRowDraggingRef.current = false;
                }}

				onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
                // Grid_Select에 공통 로직 처리를 위한 props 전달
                columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setDetailColumnState}		//컬럼 상태 저장
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
			/>
		</GridContextWrapper>
	)
}
